import type { ValuesUnion } from "../types/valuesUnion";

export enum RegrelloLocalStorageKey {
  AUTH_0_USER_EMAIL = "auth0UserEmail",
  AUTH_0_USER_KEY = "_auth0_user",
  ACTIVE_FORM_BUILDER_UUIDS = "activeFormBuilderUuids",
  BANNERS = "regrelloBanners",
  COLUMN_WIDTHS_BY_TABLE_ID = "columnWidthsByTableId",
  DATA_TAB_LAYOUT = "dataTabLayout",
  FORM_CREATED_VIA_FORM_SELECT_CREATE_OPTION = "FormOpenedInNewTabOption",
  HOME_SAVED_VIEW = "homeSavedView",
  INBOX_MINI_VIEW_IDS = "inboxMiniViewIds",
  IS_NO_THANKS_PREFERENCE_INDICATED_DOMAIN_SWITCH = "isNoThanksPreferenceIndicatedDomainSwitch",
  IS_SIDEBAR_COLLAPSED = "isSidebarCollapsed",
  JOB_LIST_HIDE_ITEM = "jobListHideItem",
  LAST_WINDOW_CURRENT_TENANT = "regrelloLastWindowCurrentTenant",
  SHOW_NEW_WORKFLOW_PROGRESS_BAR = "showNewWorkflowProgressBar",
  SPOOFING_USER_INFO = "spoofingUserInfo",
  USER_THEME = "userTheme",
  LOCALE = "locale",
  VERIFICATION_TIMESTAMP = "regrelloVerificationTimestamp",
}

export interface Auth0User {
  email: string;
  emailVerified: string;
  sub: string;
  token: string;
}

export interface HomeSavedView {
  id: number;
  tenantId: number;
  userId: number;
}

export interface SpoofingUserInfo {
  authUserId: number;
  email: string;
  tenantUserId: number;
  token: string;
}

interface StorageEntry {
  expiry: number;
  value: boolean;
}

export interface StorageItem {
  banners: Record<string, StorageEntry>;
  buster: string;
}

export interface FormOpenedInNewTabOption {
  uuid: string;
}

export type UserTheme = "light" | "dark" | "system";

export type DataTabLayout = "continuous" | "stageAndTask";

export type RegrelloLocalStorageValueMap = {
  [RegrelloLocalStorageKey.AUTH_0_USER_EMAIL]: string;
  [RegrelloLocalStorageKey.AUTH_0_USER_KEY]: Auth0User;
  [RegrelloLocalStorageKey.ACTIVE_FORM_BUILDER_UUIDS]: string[];
  [RegrelloLocalStorageKey.BANNERS]: StorageItem;
  [RegrelloLocalStorageKey.COLUMN_WIDTHS_BY_TABLE_ID]: string;
  [RegrelloLocalStorageKey.DATA_TAB_LAYOUT]: DataTabLayout;
  [RegrelloLocalStorageKey.FORM_CREATED_VIA_FORM_SELECT_CREATE_OPTION]: FormOpenedInNewTabOption | undefined;
  [RegrelloLocalStorageKey.HOME_SAVED_VIEW]: HomeSavedView;
  [RegrelloLocalStorageKey.INBOX_MINI_VIEW_IDS]: number[];
  [RegrelloLocalStorageKey.VERIFICATION_TIMESTAMP]: string;

  /**
   * (krashanoff): Though typed as a string or boolean, this should be an ISO datetime
   * that is converted using `date-fns`'s `parseISO`.
   */
  [RegrelloLocalStorageKey.IS_NO_THANKS_PREFERENCE_INDICATED_DOMAIN_SWITCH]: string | boolean;
  [RegrelloLocalStorageKey.IS_SIDEBAR_COLLAPSED]: string;
  [RegrelloLocalStorageKey.JOB_LIST_HIDE_ITEM]: number[];
  [RegrelloLocalStorageKey.LAST_WINDOW_CURRENT_TENANT]: number;
  [RegrelloLocalStorageKey.SPOOFING_USER_INFO]: SpoofingUserInfo;
  [RegrelloLocalStorageKey.SHOW_NEW_WORKFLOW_PROGRESS_BAR]: boolean;
  [RegrelloLocalStorageKey.USER_THEME]?: UserTheme;
  [RegrelloLocalStorageKey.LOCALE]?: string;
};

const PERSIST_ACROSS_LOGOUT_KEYS: RegrelloLocalStorageKey[] = [
  RegrelloLocalStorageKey.DATA_TAB_LAYOUT,
  RegrelloLocalStorageKey.INBOX_MINI_VIEW_IDS,
  RegrelloLocalStorageKey.LOCALE,
  RegrelloLocalStorageKey.USER_THEME,
];

export function localStorageGetTyped<K extends RegrelloLocalStorageKey>(
  key: K,
): RegrelloLocalStorageValueMap[K] | undefined {
  // (clewis): Return undefined instead of null so we don't have two types of empty values leaking
  // around our codebase.
  //
  if (
    key === RegrelloLocalStorageKey.AUTH_0_USER_KEY ||
    key === RegrelloLocalStorageKey.BANNERS ||
    key === RegrelloLocalStorageKey.HOME_SAVED_VIEW ||
    key === RegrelloLocalStorageKey.INBOX_MINI_VIEW_IDS ||
    key === RegrelloLocalStorageKey.SPOOFING_USER_INFO ||
    key === RegrelloLocalStorageKey.JOB_LIST_HIDE_ITEM ||
    key === RegrelloLocalStorageKey.LAST_WINDOW_CURRENT_TENANT ||
    key === RegrelloLocalStorageKey.ACTIVE_FORM_BUILDER_UUIDS
  ) {
    // eslint-disable-next-line no-restricted-properties
    const itemData = localStorage.getItem(key);
    if (itemData != null) {
      try {
        return (JSON.parse(itemData) as RegrelloLocalStorageValueMap[K]) ?? undefined;
      } catch {
        // Invalid item, remove it
        localStorage.removeItem(key);
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  // eslint-disable-next-line no-restricted-properties
  return (localStorage.getItem(key) as RegrelloLocalStorageValueMap[K]) ?? undefined;
}

export function localStorageSetTyped<K extends RegrelloLocalStorageKey>(
  key: K,
  value: RegrelloLocalStorageValueMap[K],
): void {
  // eslint-disable-next-line no-restricted-properties
  localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
}

export function localStorageClear() {
  // Build a map from keys to values.
  const keyValueMap = PERSIST_ACROSS_LOGOUT_KEYS.reduce(
    (acc, key) => {
      const value = localStorageGetTyped(key);
      acc[key] = value;
      return acc;
    },
    {} as Record<RegrelloLocalStorageKey, ValuesUnion<RegrelloLocalStorageValueMap>>,
  );

  // Clear the local storage.
  // eslint-disable-next-line no-restricted-properties
  localStorage.clear();

  // Replace the values that should persist across logout.
  for (const [key, value] of Object.entries(keyValueMap)) {
    localStorageSetTyped(key as RegrelloLocalStorageKey, value);
  }
}
